import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Share, X, Copy, Check } from 'lucide-react';
import { copyText, isWeChatBrowser } from '../utils/wechat';

/**
 * iOS 从浏览器进入时的一次性「放到桌面」提示。
 *
 * 为什么需要：用 iPhone 的家人多半是被一条邀请链接带进来的——注册完、说上话，
 * 然后关掉。下次微信里翻不到那条链接，就等于这个 App 没了。安卓用户有桌面图标，
 * iOS 用户必须有等效的东西，否则「亲缘圈层」对家人只是一次性页面。
 *
 * 只在一处出现：iOS + 未安装到桌面 + 没关过。关掉后永久记住，不骚扰。
 *
 * 文案上刻意不提「登录」：把网页放到桌面这一步本来就不需要登录，要求家人
 * 「去 Safari 登录一次」是白白的摩擦（尤其她刚在微信里用短信验证码注册过）。
 * 登录推迟到她第一次从桌面点开的时刻，一次就好、之后长期有效。
 */

const KEY = 'pengji_ios_homescreen_hint_dismissed_v1';

export default function InstallHint() {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [inWeChat, setInWeChat] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    if (!isIOS) return;
    // 已经是从桌面图标进来的，不需要提示
    const standalone =
      navigator.standalone === true ||
      window.matchMedia?.('(display-mode: standalone)')?.matches;
    if (standalone) return;
    try {
      if (localStorage.getItem(KEY) === '1') return;
    } catch {
      /* 隐私模式下 localStorage 可能不可用，此时仍显示一次，不影响主流程 */
    }
    setInWeChat(isWeChatBrowser(ua));
    setShow(true);
  }, []);

  if (!show) return null;

  // 刚加入圈层的落点带 ?onboarding=1，此时聊天页正在显示「欢迎加入自己人的圈」卡片。
  // 两条引导同时出现会互相削弱，所以让欢迎卡先说完——用户关掉它（参数被移除）后这里再出现。
  // 依赖 location 变化触发重渲染，无需回调或全局状态。
  if (new URLSearchParams(location.search).get('onboarding') === '1') return null;

  const dismiss = () => {
    try {
      localStorage.setItem(KEY, '1');
    } catch {
      /* 忽略 */
    }
    setShow(false);
  };

  const onCopy = async () => {
    // 复制站点首页而不是当前页：加到桌面只需要一个入口，首页与 manifest 的 start_url 一致，
    // 也避免把带参数的页面地址（可能有邀请码）留在剪贴板里。
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const ok = await copyText(`${origin}/`);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  return (
    <div className="sticky top-0 z-40 border-b border-primary-500/25 bg-primary-500/[0.11] backdrop-blur-md">
      <div className="mx-auto flex w-full max-w-3xl items-start gap-2.5 px-3 py-2.5">
        <Share size={16} strokeWidth={2.2} className="mt-0.5 shrink-0 text-primary-600" />
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="block w-full text-left"
          >
            <span className="text-[13px] font-semibold text-[#7A3208]">
              把朋际放到手机桌面，下次一点就进
            </span>
            <span className="ml-1 text-[12px] text-[#8A5230] underline-offset-2">
              {expanded ? '收起' : '怎么做？'}
            </span>
          </button>
          {expanded && (
            <>
              <ol className="mt-2 space-y-1.5 text-[12.5px] leading-relaxed text-[#6B3A14]">
                <li>
                  1．用 Safari 打开 qs.cn
                  {inWeChat ? '（微信里点右上角「···」→「在默认浏览器打开」）' : ''}
                  ——这一步不用登录。
                </li>
                <li>2．点底部「分享」按钮（方框带向上箭头）。</li>
                <li>3．选「添加到主屏幕」→ 添加。桌面上就有朋际图标了。</li>
              </ol>
              <p className="mt-1.5 text-[11.5px] leading-relaxed text-[#8A5230]">
                装好以后第一次从桌面图标点开时，用用户名和密码登录一次就行，之后一直有效。
              </p>
              {inWeChat && (
                <button
                  type="button"
                  onClick={onCopy}
                  className="mb-1 mt-2 inline-flex items-center gap-1.5 rounded-lg border border-[#8A5230]/30 bg-white/50 px-2.5 py-1.5 text-[12px] font-semibold text-[#7A3208] active:scale-[0.98]"
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  {copied ? '已复制，去 Safari 粘贴' : '复制当前网址'}
                </button>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="不再提示"
          className="mt-0.5 shrink-0 rounded-full p-1 text-[#8A5230] transition hover:bg-black/5"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
